import { bindAll } from 'bind-event-listener';

import { INITIAL_ANCHOR_SETTLING_WINDOW_MS } from './constants';
import type { AnchorRefs, Args } from './types';
import { USER_SCROLL_CANCEL_EVENTS } from './USER_SCROLL_CANCEL_EVENTS';
import { USER_SCROLL_INTENT_KEYS } from './USER_SCROLL_INTENT_KEYS';

export const startInitialBottomAnchor = (
	{
		enabled,
		conversationId,
		totalSize,
		containerRef,
		scrollToBottomGeneration,
		onInitialAnchorApplied,
	}: Args,
	{
		hasInitialScrollSettledRef,
		lastSettledConversationIdRef,
		lastScrollToBottomGenerationRef,
	}: AnchorRefs,
): (() => void) | undefined => {
	if (!enabled) {
		return;
	}

	// Reopen the settling window when the conversation changes or when a
	// new scroll-to-bottom is requested (e.g. user sent a message).
	if (lastSettledConversationIdRef.current !== conversationId) {
		hasInitialScrollSettledRef.current = false;
		lastSettledConversationIdRef.current = conversationId;
	}
	if (lastScrollToBottomGenerationRef.current !== scrollToBottomGeneration) {
		hasInitialScrollSettledRef.current = false;
		lastScrollToBottomGenerationRef.current = scrollToBottomGeneration;
	}

	if (hasInitialScrollSettledRef.current) {
		return;
	}

	const settle = () => {
		if (hasInitialScrollSettledRef.current) {
			return;
		}
		hasInitialScrollSettledRef.current = true;
	};

	const container = containerRef.current;
	if (!container || typeof container.scrollTo !== 'function') {
		settle();
		onInitialAnchorApplied?.();
		return;
	}

	let frame: number | null = null;
	let isCancelled = false;
	let lastScrollHeight = -1;
	const settlingWindowStart = performance.now();

	const anchorToBottom = () => {
		const nextScrollHeight = container.scrollHeight;
		// Skip until the container has real measurements. Anchoring to 0 (or
		// NaN) briefly puts the scroll position at the top, which cancels the
		// subsequent RAF re-anchor because our own scrollTo dispatches a
		// scroll event that other listeners may treat as user intent.
		if (!Number.isFinite(nextScrollHeight) || nextScrollHeight <= 0) {
			return;
		}
		if (nextScrollHeight === lastScrollHeight) {
			return;
		}
		lastScrollHeight = nextScrollHeight;
		container.scrollTo({ top: nextScrollHeight, behavior: 'instant' });
	};

	const markUserInteracted = () => {
		settle();
	};

	const markKeyboardScrollIntent = (event: KeyboardEvent) => {
		if (USER_SCROLL_INTENT_KEYS.has(event.key)) {
			settle();
		}
	};

	const unbindUserScrollCancelEvents = bindAll(container, [
		...USER_SCROLL_CANCEL_EVENTS.map((eventName) => ({
			type: eventName,
			listener: markUserInteracted,
			options: { passive: true },
		})),
		{
			type: 'keydown',
			listener: markKeyboardScrollIntent,
			options: { passive: true },
		},
	]);

	anchorToBottom();
	onInitialAnchorApplied?.();

	const keepAnchoredDuringSettlingWindow = (timestamp: DOMHighResTimeStamp) => {
		if (isCancelled || hasInitialScrollSettledRef.current) {
			return;
		}

		anchorToBottom();

		if (timestamp - settlingWindowStart >= INITIAL_ANCHOR_SETTLING_WINDOW_MS) {
			settle();
			return;
		}

		frame = requestAnimationFrame(keepAnchoredDuringSettlingWindow);
	};

	frame = requestAnimationFrame(keepAnchoredDuringSettlingWindow);
	return () => {
		isCancelled = true;
		if (frame !== null) {
			cancelAnimationFrame(frame);
		}
		unbindUserScrollCancelEvents();
	};
};
